// frontend/src/views/AuthenticatedDashboard.jsx

import { useEffect, useState, useCallback } from "react";

// Librerías externas
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Hooks y servicios
import { getJWTSession } from "../utils/useJWTSession";
import { deleteGroup } from '../services/groupService';
import { sendMagicLink } from '../services/authService';

// Lógica de sesión
import { setStoredSession } from '../hooks/useHashSession'

// Traducción
import { useTranslation } from "../i18n";

// Componentes
import GroupCreatorModal from '../components/GroupCreatorModal';
import GroupDeleteModal from '../components/GroupDeleteModal';

export default function AuthenticatedDashboard() {
  const { _ } = useTranslation();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("");
  const [showCreator, setShowCreator] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [linkSentMessage, setLinkSentMessage] = useState(null);
  const [debugError, setDebugError] = useState(null);

  const fetchGroups = useCallback(() => {
    const token = getJWTSession();
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUserName(payload.email);
    } catch (e) {
      navigate("/login");
      return;
    }

    fetch(`${process.env.REACT_APP_API_BASE}/api/user/authenticated`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        if (res.status === 403 || res.status === 401) {
          navigate("/login");
        } else if (!res.ok) {
          throw new Error("Failed to load groups");
        } else {
          const data = await res.json();
          setGroups(data);
        }
      })
      .catch((err) => {
        setError(_("Unable to load your groups."));
      });
  }, [navigate, _]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleDeleteGroup = async (groupId) => {
    try {
      await deleteGroup(groupId);
      setGroups(prev => prev.filter(g => g.id !== groupId));
      setGroupToDelete(null);
    } catch (err) {
      setError(_("Error deleting group. Please try again."));
      setGroupToDelete(null);
    }
  };

  const handleGroupCreated = () => {
    setShowCreator(false);
    setTimeout(() => fetchGroups(), 50);
  };

  /**
 * Envía una solicitud al backend para reenviar el enlace mágico
 * a un grupo específico.
 * @param {string} email - Correo del responsable del grupo.
 */
  const handleSendMagicLink = async (email) => {
    try {
      if (!email) {
        throw new Error("Email address is missing.");
      }

      await sendMagicLink(email);

      // Si la solicitud es exitosa, muestra un mensaje de éxito.
      setLinkSentMessage(
        _("Access link sent. Tell the group owner to check their email.")
      );
    } catch (err) {
      // Si hay un error, muestra un mensaje de error.
      setLinkSentMessage(_("Failed to send magic link. Please try again."));
    } finally {
      // Oculta el mensaje de estado después de 5 segundos.
      setTimeout(() => setLinkSentMessage(null), 5000);
    }
  };

  return (
    <div className="container">
      <div className="flex justify-between items-start mb-4">
        <div className="flex flex-row items-center gap-2">
          <p className="header-title">{_("My Groups")}</p>
          <button
            className="icon-button text-green-600"
            title={_("Create new group")}
            onClick={() => setShowCreator(true)}
          >
            <FontAwesomeIcon icon="plus" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="flex justify-between items-start mb-4">
        <div>
          <p className="header-sub">
            {_("Responsible")}: <span className="font-semibold">{userName}</span>
          </p>
          <p className="header-sub">
            {_("Total Groups")}: <span className="font-semibold">{groups.length}</span>
          </p>
        </div>
      </div>

      {error && <p className="error-text">{error}</p>}
      {linkSentMessage && <p className="success-text">{linkSentMessage}</p>}

      {groups.length === 0 ? (
        <p className="text-center text-sm text-gray-400 dark:text-gray-500">
          {_('You have no groups yet.')}
        </p>
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="table-default text-sm bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md">
            <thead>
              <tr className="table-head-row">
                <th>{_("Group Name")}</th>
                <th>{_("Hash")}</th>
                <th>{_("Created At (male)")}</th>
                <th className="!text-center">{_("Status")}</th>
                <th>{_("# Members")}</th>
                <th className="!text-center">{_("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const stateIcons = {
                  'active': "check-circle",
                  'blocked': "ban",
                  'revoked': "minus-circle",
                  'pending': "clock",
                  'closed': "lock",
                };
                const iconToDisplay = stateIcons[g.state] || "circle-question";

                return (
                  <tr key={g.id} className="table-row">
                    <td>{g.group_name}</td>
                    <td>{g.main_hash}</td>
                    <td>{new Date(g.created_at).toLocaleDateString()}</td>
                    <td className="capitalize text-center">
                      <FontAwesomeIcon
                        icon={iconToDisplay}
                        title={_(g.state + ' (male)' || "unknown")}
                      />
                    </td>
                    <td className="text-center">
                      {g.total_members}
                    </td>
                    <td className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          sessionStorage.setItem('active_group_hash', g.main_hash);
                          sessionStorage.setItem('group_origin', 'dashboard');
                          navigate('/members');
                        }}
                        className="icon-button text-blue-500"
                        title={_('Manage Members')}
                      >
                        <FontAwesomeIcon icon="users" aria-hidden="true" />
                      </button>
                      {/* Botón para enviar el enlace mágico */}
                      <button
                        onClick={() => handleSendMagicLink(g.owner_email)}
                        className="icon-button text-indigo-500"
                        title={_('Send Magic Link')}
                      >
                        <FontAwesomeIcon icon="envelope" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => {
                          setGroupToDelete(g);
                        }}
                        className="icon-button text-red-500"
                        title={_('Delete Group')}
                      >
                        <FontAwesomeIcon icon="trash" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreator && (
        <GroupCreatorModal
          onClose={() => {
            setShowCreator(false);
          }}
          onSave={handleGroupCreated}
        />
      )}
      {groupToDelete && (
        <GroupDeleteModal
          group={groupToDelete}
          onClose={() => {
            setGroupToDelete(null);
          }}
          onConfirm={handleDeleteGroup}
        />
      )}
    </div>
  );
}